import React, { useState, useRef } from 'react';
import {
  ArrowLeft,
  Camera,
  User,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { ensureBrowserCompatibleImage } from '../../lib/imageUtils';

type ToastState = { message: string; type: 'success' | 'error' } | null;

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  if (!toast) return null;
  return (
    <div className={cn(
      'fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center space-x-2 px-5 py-3 rounded-xl shadow-lg text-sm font-medium max-w-sm',
      toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
    )}>
      {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-2 text-current opacity-50 hover:opacity-100">&times;</button>
    </div>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, userProfile, updateProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<ToastState>(null);
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoURL = user?.photoURL;

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      showToast('Display name cannot be empty', 'error');
      return;
    }
    try {
      setSaving(true);
      await updateProfile({ displayName: displayName.trim() });
      showToast('Name updated successfully', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to update name', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const isHeic = /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (!file.type.startsWith('image/') && !isHeic) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be less than 5MB', 'error');
      return;
    }

    try {
      setUploadingPhoto(true);
      const compatible = await ensureBrowserCompatibleImage(file);
      const storageRef = ref(storage, `users/${user.uid}/profile-photo`);
      await uploadBytes(storageRef, compatible);
      const downloadURL = await getDownloadURL(storageRef);
      await updateProfile({ photoURL: downloadURL });
      showToast('Photo updated successfully', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const initials = (user?.displayName || user?.email || 'U').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors">
              <ArrowLeft className="h-6 w-6 text-heading" />
            </button>
            <h1 className="ml-4 text-xl font-serif text-heading">Edit Profile</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Profile Photo */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center text-3xl font-serif text-primary overflow-hidden">
              {photoURL ? (
                <img src={photoURL} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>
          <p className="text-sm text-body mt-3">Tap the camera icon to change your photo</p>
        </div>

        {/* Display Name */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Personal Information
          </h2>
          <Card>
            <CardContent className="p-5">
              <form onSubmit={handleSaveName} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-heading mb-1.5">Display Name</label>
                  <Input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-heading mb-1.5">Email</label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-body mt-1.5">Email cannot be changed. Contact support for help.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-heading mb-1.5">Role</label>
                  <Input
                    type="text"
                    value={userProfile?.role || 'shopper'}
                    disabled
                    className="bg-gray-50 capitalize"
                  />
                </div>
                <Button
                  type="submit"
                  loading={saving}
                  disabled={displayName.trim() === (user?.displayName || '')}
                  className="w-full"
                >
                  <User className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
